import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConfigField, ReferencePreview, ServiceOption } from '../../data/types';
import { getReferencePreview } from '../../data/reference';
import { TokenField } from './TokenField';
import { buildReferenceKeyItems, parseLiteralReferenceKey, type ReferenceOption, type VariableSection } from './variableSections';

interface PropertyConfigFieldsProps {
  schema: ConfigField[];
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** The property's Default value; a bare `{{@Key}}` here can name one previewable table entry. */
  defaultValue?: string;
  /** `table`-kind reference entries, used to preview `columns` for a reference-sourced Table property. */
  tableReferenceOptions?: ReferenceOption[];
  /** `image`-use reference entries (file images and imageCollections), used to offer `collectionItem`. */
  imageReferenceOptions?: ReferenceOption[];
  /** Same-template properties a `linkedPropertyType`-bound field may name, keyed by property type. */
  linkedPropertyOptions?: (propertyType: string) => ReferenceOption[];
  /** DT-12: configured services offered as a picker for the `serviceId` config field. */
  services?: ServiceOption[];
  /** The same `{{Token}}` picker sections the Default Value field uses, reused for `inputValues` mappings. */
  variableSections?: VariableSection[];
  /** The property type's valid `source` values; renders `source` as a select instead of free text. */
  valueSources?: string[];
  /** Writes a picked reference key straight into the property's Default value, as `{{@key}}`. */
  onDefaultValueChange?: (next: string) => void;
}

const input =
  'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

/** Config field names are registry identifiers (`widthPx`, `serviceId`), not display text. */
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  widthPx: 'Width (px)',
  systemPrompt: 'System prompt',
  serviceId: 'Service',
  yesLabel: 'Label when yes',
  noLabel: 'Label when no',
  allowCustomValue: 'Allow custom value',
  customMinLength: 'Custom value min length',
  customMaxLength: 'Custom value max length',
  linkedCompanyProperty: 'Linked company',
  collectionItem: 'Specific image',
  maxSlides: 'Max slides'
};

function humanizeFieldName(name: string): string {
  const override = FIELD_LABEL_OVERRIDES[name];
  if (override) return override;
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Meaningless until `allowCustomValue` is on, so hide them rather than show two dead inputs. */
const CUSTOM_VALUE_LENGTH_FIELDS = new Set(['customMinLength', 'customMaxLength']);

/** Rendered from the registry's config schema, so a new type needs no code here. */
export function PropertyConfigFields({
  schema,
  config,
  onChange,
  defaultValue,
  tableReferenceOptions,
  imageReferenceOptions,
  linkedPropertyOptions,
  services,
  variableSections,
  valueSources,
  onDefaultValueChange
}: PropertyConfigFieldsProps) {
  const { t } = useTranslation();

  // Only a bare {{@Key}} (no $variable) names one exact entry at config time; a dynamic
  // default's real key is only known at generation time, so there's nothing to preview.
  const literalKey = defaultValue ? parseLiteralReferenceKey(defaultValue) : null;
  const columnsSource = schema.find((field) => field.name === 'columns')
    ? String(config.source ?? 'upload')
    : null;
  const tableEntry =
    columnsSource === 'reference' && literalKey && tableReferenceOptions
      ? tableReferenceOptions.find((option) => option.key === literalKey)
      : undefined;
  const hasCollectionItemField = schema.some((field) => field.name === 'collectionItem');
  const imageEntry =
    hasCollectionItemField && literalKey && imageReferenceOptions
      ? imageReferenceOptions.find((option) => option.key === literalKey)
      : undefined;
  // Only one of these can apply per property type, so they can share one preview fetch.
  const previewEntry = tableEntry ?? imageEntry;

  // Keyed by scope+key so a stale fetch from a previous entry is never shown for the current one.
  const [previewState, setPreviewState] = useState<{ id: string; data: ReferencePreview | null } | null>(null);

  useEffect(() => {
    if (!previewEntry) return;
    const id = `${previewEntry.scope}:${previewEntry.key}`;
    let cancelled = false;
    getReferencePreview(previewEntry.scope, previewEntry.key)
      .then((data) => {
        if (!cancelled) setPreviewState({ id, data });
      })
      .catch(() => {
        if (!cancelled) setPreviewState({ id, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [previewEntry]);

  const preview =
    previewState && previewEntry && previewState.id === `${previewEntry.scope}:${previewEntry.key}`
      ? previewState.data
      : null;

  // Self-heals config saved under a schema that has since dropped a field (e.g. a removed
  // `referenceKey`) - otherwise the stale key sits unseen until it fails the next save.
  useEffect(() => {
    const known = new Set(schema.map((field) => field.name));
    const stale = Object.keys(config).filter((name) => !known.has(name));
    if (stale.length === 0) return;
    const next = { ...config };
    for (const name of stale) delete next[name];
    onChange(next);
  }, [schema, config, onChange]);

  // A required field shows its default in the picker, but that default only reaches `config` once
  // the user touches the control - backfill it so an untouched save still submits a valid config.
  useEffect(() => {
    const missing = schema.filter((field) => {
      if (!field.required || config[field.name] !== undefined) return false;
      return field.default !== null || (field.name === 'source' && !!valueSources?.length);
    });
    if (missing.length === 0) return;
    const next = { ...config };
    for (const field of missing) {
      next[field.name] = field.name === 'source' && valueSources ? (field.default ?? valueSources[0]) : field.default;
    }
    onChange(next);
  }, [schema, config, onChange, valueSources]);

  if (schema.length === 0) return null;

  const set = (name: string, value: unknown) => onChange({ ...config, [name]: value });

  const remove = (name: string) => {
    const next = { ...config };
    delete next[name];
    onChange(next);
  };

  // Image/Table/List/DocumentDictionary carry a `source` field; their serviceId/inputValues only
  // apply once the author has actually picked "service" there. ServiceLookup has no `source` field
  // at all, so it keeps showing them unconditionally.
  const hasSourceField = schema.some((field) => field.name === 'source');
  const showServiceFields = !hasSourceField || config.source === 'service';

  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      {schema.map((field) => {
        const label = `${humanizeFieldName(field.name)}${field.required ? ' *' : ''}`;
        const value = config[field.name];

        if (CUSTOM_VALUE_LENGTH_FIELDS.has(field.name) && config.allowCustomValue !== true) return null;

        if (field.name === 'serviceId' && services) {
          if (!showServiceFields) return null;

          const active = services.filter((service) => service.isActive);
          const selected = services.find((service) => service.id === value);
          return (
            <div key={field.name} className="sm:col-span-2">
              <label className="block text-sm">
                <span className="font-medium text-foreground">{label}</span>
                <select
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => {
                    const next = { ...config };
                    if (event.target.value === '') delete next[field.name];
                    else next[field.name] = event.target.value;
                    // A different service means any previously-mapped input values are stale.
                    delete next.inputValues;
                    onChange(next);
                  }}
                  className={input}
                >
                  <option value="">{t('properties.config.chooseService')}</option>
                  {active.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                  {selected && !selected.isActive ? (
                    <option value={selected.id}>{t('properties.config.serviceInactive', { name: selected.name })}</option>
                  ) : null}
                </select>
              </label>
              <span className="mt-1 block text-xs text-muted-foreground">
                {active.length === 0 ? t('properties.config.noServicesHint') : t('properties.config.serviceHint')}
              </span>
            </div>
          );
        }

        if (field.name === 'inputValues') {
          if (!showServiceFields) return null;

          const selectedService = services?.find((service) => service.id === config.serviceId);
          const parameters = selectedService?.inputParameters ?? [];
          if (parameters.length === 0) return null;

          const map = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, string>) : {};
          const setParam = (name: string, next: string) => {
            const nextMap = { ...map };
            if (next === '') delete nextMap[name];
            else nextMap[name] = next;
            if (Object.keys(nextMap).length === 0) remove(field.name);
            else set(field.name, nextMap);
          };

          return (
            <div key={field.name} className="sm:col-span-2">
              <span className="font-medium text-foreground">{t('properties.config.inputValues')}</span>
              <p className="mt-1 text-xs text-muted-foreground">{t('properties.config.inputValuesHint')}</p>
              <div className="mt-2 grid gap-3">
                {parameters.map((name) => (
                  <TokenField
                    key={name}
                    label={name}
                    value={map[name] ?? ''}
                    onChange={(next) => setParam(name, next)}
                    transforms={[]}
                    sections={variableSections ?? []}
                  />
                ))}
              </div>
            </div>
          );
        }

        if (field.linkedPropertyType && linkedPropertyOptions) {
          const options = linkedPropertyOptions(field.linkedPropertyType);
          const typeLabel = t(`propertyTypes.${field.linkedPropertyType}`);
          return (
            <div key={field.name} className="sm:col-span-2">
              <TokenField
                label={label}
                value={value === undefined || value === null ? '' : String(value)}
                onChange={(next) => (next === '' ? remove(field.name) : set(field.name, next))}
                transforms={[]}
                sections={
                  options.length === 0
                    ? []
                    : [
                        {
                          id: `linked:${field.linkedPropertyType}`,
                          kind: 'reference',
                          title: typeLabel,
                          items: buildReferenceKeyItems(options)
                        }
                      ]
                }
              >
                <span className="mt-1 block text-xs text-muted-foreground">
                  {options.length === 0
                    ? t('properties.config.linkedPropertyMissing', { type: typeLabel })
                    : t('properties.config.linkedPropertyHint', { type: typeLabel })}
                </span>
              </TokenField>
            </div>
          );
        }

        if (field.kind === 'textList') {
          // The reference table already carries real columns; typing them again would just drift out of sync.
          if (field.name === 'columns' && tableEntry && preview?.entry.kind === 'table') {
            return (
              <div key={field.name} className="sm:col-span-2 text-sm">
                <span className="font-medium text-foreground">{label}</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {preview.columns.map((column) => (
                    <span
                      key={column}
                      className="rounded-full border border-input bg-muted px-2.5 py-1 text-xs text-foreground"
                    >
                      {column}
                    </span>
                  ))}
                </div>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {t('properties.config.columnsFromReference', { key: tableEntry.key })}
                </span>
              </div>
            );
          }

          return (
            <label key={field.name} className="block text-sm sm:col-span-2">
              <span className="font-medium text-foreground">{label}</span>
              <input
                type="text"
                value={Array.isArray(value) ? value.join(', ') : ''}
                onChange={(event) =>
                  set(
                    field.name,
                    event.target.value
                      .split(',')
                      .map((part) => part.trim())
                      .filter(Boolean)
                  )
                }
                className={input}
              />
              {field.name === 'columns' ? (
                <span className="mt-1 block text-xs text-muted-foreground">
                  {t(config.source === 'manual' ? 'properties.config.columnsHintManual' : 'properties.config.columnsHint')}
                </span>
              ) : null}
            </label>
          );
        }

        // Nothing to pick from until the Default value names an actual imageCollection entry.
        if (field.name === 'collectionItem') {
          if (!imageEntry || preview?.entry.kind !== 'imageCollection') return null;

          // Same config field, two roles: when `source` is itself "collection" this pins the
          // property's own value; otherwise the Default value is only a fallback (e.g. for a
          // `service` source), so the label/hint must say so rather than imply it's live.
          const isPrimarySource = config.source === 'collection';

          return (
            <div key={field.name} className="sm:col-span-2">
              <label className="block text-sm">
                <span className="font-medium text-foreground">
                  {isPrimarySource ? label : t('properties.config.collectionItemFallbackLabel')}
                </span>
                <select
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => (event.target.value === '' ? remove(field.name) : set(field.name, event.target.value))}
                  className={input}
                >
                  <option value="">{t('properties.config.collectionItemFirst')}</option>
                  {preview.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.fileName}
                    </option>
                  ))}
                </select>
              </label>
              <span className="mt-1 block text-xs text-muted-foreground">
                {t(
                  isPrimarySource ? 'properties.config.collectionItemFromReference' : 'properties.config.collectionItemFallbackHint',
                  { key: imageEntry.key }
                )}
              </span>
            </div>
          );
        }

        if (field.name === 'source' && valueSources && valueSources.length > 0) {
          const sourceValue = typeof value === 'string' && value ? value : (field.default ?? valueSources[0]);
          // Only Image declares `collectionItem`; only its `collection`/`reference` sources need a picker.
          const showEntryPicker =
            hasCollectionItemField && (sourceValue === 'collection' || sourceValue === 'reference') && onDefaultValueChange;
          const entryOptions = showEntryPicker
            ? (imageReferenceOptions ?? []).filter((option) =>
                sourceValue === 'collection' ? option.kind === 'imageCollection' : option.kind !== 'imageCollection'
              )
            : [];

          return (
            <div key={field.name} className={showEntryPicker ? 'sm:col-span-2' : undefined}>
              <label className="block text-sm">
                <span className="font-medium text-foreground">{label}</span>
                <select
                  value={sourceValue}
                  onChange={(event) => set(field.name, event.target.value)}
                  className={input}
                >
                  {valueSources.map((option) => (
                    <option key={option} value={option}>
                      {t(`properties.config.sources.${option}`)}
                    </option>
                  ))}
                </select>
              </label>

              {showEntryPicker ? (
                <div className="mt-3">
                  <label className="block text-sm">
                    <span className="font-medium text-foreground">
                      {t(`properties.config.sources.${sourceValue}`)}
                    </span>
                    <select
                      value={literalKey ?? ''}
                      onChange={(event) =>
                        onDefaultValueChange(event.target.value === '' ? '' : `{{@${event.target.value}}}`)
                      }
                      className={input}
                    >
                      <option value="">{t('properties.config.chooseImageEntry')}</option>
                      {entryOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.title || option.key}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {entryOptions.length === 0
                      ? t(
                          sourceValue === 'collection'
                            ? 'properties.config.noCollectionsHint'
                            : 'properties.config.noImageReferencesHint'
                        )
                      : t('properties.config.imageEntryHint')}
                  </span>
                </div>
              ) : null}
            </div>
          );
        }

        if (field.kind === 'boolean') {
          return (
            <label key={field.name} className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={value === true}
                onChange={(event) => set(field.name, event.target.checked)}
                className="size-4 rounded border-input"
              />
              <span className="font-medium text-foreground">{label}</span>
            </label>
          );
        }

        if (field.name === 'systemPrompt') {
          return (
            <label key={field.name} className="block text-sm sm:col-span-2">
              <span className="font-medium text-foreground">{label}</span>
              <textarea
                value={value === undefined || value === null ? '' : String(value)}
                placeholder={field.default ?? ''}
                rows={4}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === '') {
                    const next = { ...config };
                    delete next[field.name];
                    onChange(next);
                    return;
                  }
                  set(field.name, raw);
                }}
                className={`${input} resize-y`}
              />
            </label>
          );
        }

        return (
          <label key={field.name} className="block text-sm">
            <span className="font-medium text-foreground">{label}</span>
            <input
              type={field.kind === 'number' ? 'number' : 'text'}
              value={value === undefined || value === null ? '' : String(value)}
              placeholder={field.default ?? ''}
              onChange={(event) => {
                const raw = event.target.value;
                if (raw === '') {
                  const next = { ...config };
                  delete next[field.name];
                  onChange(next);
                  return;
                }
                set(field.name, field.kind === 'number' ? Number(raw) : raw);
              }}
              className={input}
            />
          </label>
        );
      })}
    </div>
  );
}
